import { type QueryClient } from '@tanstack/react-query';
import { noop } from 'lodash';

import { type Clip } from '@air/api/types';

import { getPrivateMediaPlayerKey, getPublicMediaPlayerKey } from '~/constants/react-query-keys';
import { type KanbanManagerState } from '~/store/kanbanManager/types';
import { DndItemType } from '~/types/DndKit';
import { getBoardIdFromPath, getShortIdFromPath } from '~/utils/PathUtils';
import { type MediaPlayerAssetsInfo } from '../useMediaPlayerData';

const isClip = (data: unknown): data is Clip => {
  return (
    typeof data === 'object' &&
    data !== null &&
    'id' in data &&
    'assetId' in data &&
    typeof data.id === 'string' &&
    typeof data.assetId === 'string'
  );
};

export const updateKanbanMediaPlayerCache = (
  queryClient: QueryClient,
  columns: KanbanManagerState['columns'],
  workspaceId?: string,
): void => {
  const allItems = Object.values(columns)
    .flatMap((column) => column?.items ?? [])
    .filter((item) => item.dndType === DndItemType.kanbanAsset)
    .map((item) => {
      const { data } = item.apiData;
      if (!isClip(data)) {
        return null;
      }
      return {
        id: data.id,
        assetId: data.assetId,
      };
    })
    .filter((item): item is { id: string; assetId: string } => item !== null);

  const mediaPlayerData: MediaPlayerAssetsInfo = {
    itemsIds: allItems,
    hasMore: false,
    loadMore: noop,
  };

  const shortId = getShortIdFromPath(window.location.pathname);
  const boardId = getBoardIdFromPath(window.location.pathname);

  if (shortId) {
    queryClient.setQueryData(getPublicMediaPlayerKey(shortId), mediaPlayerData);
  } else if (workspaceId && boardId) {
    queryClient.setQueryData(getPrivateMediaPlayerKey(workspaceId), mediaPlayerData);
  }
};
